import { Button, Stack, Typography } from '@mui/material';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import { CONFIRM_DIALOG } from '@schooly/constants';
import { Loading, ModalConfirm, ModalPanel, Spin } from '@schooly/style';
import React, { PropsWithChildren, ReactNode, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

interface ConfirmationDialogProps
  extends PropsWithChildren<Omit<DialogProps, 'open' | 'children' | 'content'>> {
  isOpen: boolean;
  isLoading?: boolean;
  isContentLoading?: boolean;
  isDestructive?: boolean;
  shouldCloseAutomatically?: boolean;
  className?: string;
  titleTextId?: string;
  textValues?: Record<string, React.ReactNode>;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTextId?: string;
  cancelTextId?: string;
  confirmIcon?: JSX.Element;
  onClose?: () => void;
  onCancel: () => void;
  onConfirm?: () => void;
  message?: any;
  onlyCancelButton?: boolean;
  cancelButtonStartIcon?: React.ReactNode;
  content?: ReactNode;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  isLoading,
  isDestructive,
  shouldCloseAutomatically,
  className,
  titleTextId,
  textValues,
  confirmTextId = 'action-Confirm',
  cancelTextId = 'action-Cancel',
  cancelLabel,
  confirmLabel,
  onClose,
  onCancel,
  onConfirm = () => {},
  children,
  message,
  onlyCancelButton,
  cancelButtonStartIcon,
  isContentLoading,
  content,
  ...props
}) => {
  const handleConfirm = useCallback(() => {
    if (shouldCloseAutomatically) {
      onCancel();
    }

    onConfirm();
  }, [shouldCloseAutomatically, onConfirm, onCancel]);

  return (
    <ModalConfirm
      open={isOpen}
      onClose={onClose ?? onCancel}
      PaperProps={{ className, 'data-test-id': CONFIRM_DIALOG.CONTAINER }}
      fullWidth
      {...props}
    >
      <ModalPanel active>
        {isContentLoading ? (
          <Loading />
        ) : (
          <>
            {(titleTextId || message) && (
              <Typography variant="h1">
                {titleTextId ? <FormattedMessage id={titleTextId} values={textValues} /> : message}
              </Typography>
            )}
            {content}
          </>
        )}
      </ModalPanel>
      {children}
      <ModalPanel
        component={Stack}
        sx={{
          flexDirection: 'row',
          justifyContent: 'center',
          gap: 1,
          pt: 0,
          '& .MuiButton-root': { flex: '1 1 50%' },
        }}
        active
      >
        <Button
          variant={onlyCancelButton ? 'contained' : 'outlined'}
          onClick={onCancel}
          disabled={isLoading}
          startIcon={cancelButtonStartIcon}
          data-test-id={CONFIRM_DIALOG.CANCEL_BUTTON}
        >
          {cancelLabel ?? <FormattedMessage id={cancelTextId} />}
        </Button>
        {!onlyCancelButton && (
          <Button
            color={isDestructive ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={isLoading}
            startIcon={isLoading ? <Spin /> : undefined}
            data-test-id={CONFIRM_DIALOG.CONFIRM_BUTTON}
          >
            {confirmLabel ?? <FormattedMessage id={confirmTextId} />}
          </Button>
        )}
      </ModalPanel>
    </ModalConfirm>
  );
};

export default ConfirmationDialog;
